import React, { useState, useEffect } from 'react';
import { GuideLayout } from './styles';
import { Navigation } from './Navigation/Navigation';
import { Content } from './Content/Content';
import { FullQSGTemplate } from './Content/FullQSGTemplate';
import { DisclaimerProvider } from '../Disclaimer';
import { useStore } from '../../store/store';
import {
  resetQsgSearch,
  setQsgSearchTerm,
} from '../../store/slices/qsg/actions';
import { Option } from '../Input/types';
import { useHistory } from '../../hooks/use-history';
import {
  onVWBasicFilterSearchButtonClickEvent_SearchEnter,
  onVWBasicLinkClickEvent_SearchAutocomplete,
} from '../../tagging/tagging-helpers';
import { useTrackingManager } from '../../hooks/use-tracking-manager';
import { pushWithPathname, replaceWithPathname } from '../../utils/navigation-url';
import { generatePdfFromDom } from '../../utils/generate-pdf';
import { downloadBlob } from '../../utils/download';
import { Box } from '../Box';
import { deleteUrlParam } from '../../utils/navigation-url';
import {
  getQueryParams,
  getSectionFromPath,
  isGuideRoute,
  getModelPathParams,
  normalizeSectionId,
  normalizeQueryToParams,
  paramsToSearchString,
} from '../../utils/query-param-helpers';
import {
  onVWBasicLinkClickEvent_OpenGuide,
  onVWBasicLinkClickEvent_SearchResult,
  onVWBasicLinkClickEvent_NavigationMenu
} from '../../tagging/tagging-helpers';
import {
  SectionModel,
  NavigationSection,
} from '../../fetch-data/graphql/types';
import { cleanPlaintext } from '../Guide/Content/Search/helpers';


interface GuideProps {
  onReset: () => void;
  scrollToTop: () => void;
  hasInit: boolean;
}

export const Guide = ({ onReset, scrollToTop, hasInit }: GuideProps) => {
  const history = useHistory();
  const [state, dispatch] = useStore();
  const { qsgSearchTerm, qsgNavigation, selectedModel } = state.qsg;
  const trackingManager = useTrackingManager();

  const model = qsgNavigation
    ? {
        modelId: qsgNavigation.modelId,
        modelName: qsgNavigation.modelName,
        modelYear: qsgNavigation.modelYear,
      }
    : undefined;

  const [stickyHeaderToggle, setStickyHeaderToggle] = useState<boolean>(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);
  const [pdfProgress, setPdfProgress] = useState<number>(0);

  const [currentPathname, setCurrentPathname] = useState(
    history.location.pathname
  );

  useEffect(() => {
    const unlisten = history.listen((location) => {
      setCurrentPathname(location.pathname);
    });
    return unlisten;
  }, [history]);

  const sectionInPath = getSectionFromPath(currentPathname);

  const isInContentMode = Boolean(sectionInPath);
  const hasSearch = Boolean(qsgSearchTerm);
  const isSidebarOpen = !isInContentMode && !hasSearch;

  const isInsideGuide = isGuideRoute(currentPathname);
  const query = getQueryParams(history);

  const hasGeneratePdfParam = query['generate-pdf'] !== undefined;
  const isPrintMode = query['print-mode'] !== undefined;

  const handleChangeHeaderToggle = (value: boolean) => {
    setStickyHeaderToggle(value);
  };

  const changeModel = () => {
    onReset();

    const pathParams = getModelPathParams(currentPathname);
    if (pathParams.modelYear && pathParams.modelId) {
    
      const modelSelectorPath = '/';
      const query = getQueryParams(history);
      const params = normalizeQueryToParams(query);
      params['model-year'] = pathParams.modelYear;
      params.model = pathParams.modelId;
      params.search = undefined;
      params.page = undefined;
      const searchString = paramsToSearchString(params);

      pushWithPathname(history, modelSelectorPath, searchString);

      scrollToTop();
    }
  };

  const openSidebar = () => {
    const pathParams = getModelPathParams(currentPathname);
    if (pathParams.modelYear && pathParams.modelId) {
      const navigationPath = `/${pathParams.modelYear}/${pathParams.modelId}`;
      const query = getQueryParams(history);
      const params = normalizeQueryToParams(query);
      params.search = undefined;
      params.page = undefined;
      const searchString = paramsToSearchString(params);      
      pushWithPathname(history, navigationPath, searchString);

      onVWBasicLinkClickEvent_OpenGuide(
                  trackingManager,
                  false,
                  window.location.href,
                  model
                );

      scrollToTop();
    }
  };

  const goToSection = (section: SectionModel | NavigationSection) => {
    if (!section.sectionId) return;

    const pathParams = getModelPathParams(currentPathname);
    if (pathParams.modelYear && pathParams.modelId) {
      const normalizedSectionId = normalizeSectionId(section.sectionId);
      const contentPath = `/${pathParams.modelYear}/${pathParams.modelId}/${normalizedSectionId}`;
      const query = getQueryParams(history);

      const params = normalizeQueryToParams(query);
      params.search = undefined;
      params.page = undefined;
      const searchString = paramsToSearchString(params);

      pushWithPathname(history, contentPath, searchString);
      onVWBasicLinkClickEvent_NavigationMenu(
        trackingManager,
        section,
        false,
        window.location.href,
        model,
        
      );
      scrollToTop();
    }
  };

  const goToSectionFromSearch = (sectionId: string | null, searchTerm: string, result, resultRelativeIndex, totalResults) => {
    if (!sectionId) return;

    const pathParams = getModelPathParams(currentPathname);
    if (pathParams.modelYear && pathParams.modelId) {
      const normalizedSectionId = normalizeSectionId(sectionId);
      const contentPath = `/${pathParams.modelYear}/${pathParams.modelId}/${normalizedSectionId}`;
      const query = getQueryParams(history);

      const params = normalizeQueryToParams(query);
      params.search = undefined;
      params.page = undefined;
      const searchString = paramsToSearchString(params);

      pushWithPathname(history, contentPath, searchString);
      onVWBasicLinkClickEvent_SearchResult(
          trackingManager,
          searchTerm,
          result,
          false,
          resultRelativeIndex,
          totalResults,
          window.location.href,
          model
          
        );
      scrollToTop();
    }
  };

  const onSubmitSearch = (
    option: Option | null,
    isAutocompleteClick?: boolean
  ) => {
    if (option && option.value) {
      const sanitizedValue  = cleanPlaintext(option.value);
      onVWBasicFilterSearchButtonClickEvent_SearchEnter(
        trackingManager,
        option.value,
        model
      );

      

      if (sanitizedValue !== qsgSearchTerm) dispatch(resetQsgSearch());
      
      const pathParams = getModelPathParams(currentPathname);

      const navigationPath = `/${pathParams.modelYear}/${pathParams.modelId}`;

      const query = getQueryParams(history);
      const params = normalizeQueryToParams(query);
      params.search = sanitizedValue;
      params.page = undefined; // Reset to page 1 when new search
      const searchString = paramsToSearchString(params);

      pushWithPathname(history, navigationPath, decodeURI(searchString));
      
      if (isAutocompleteClick) {
        onVWBasicLinkClickEvent_SearchAutocomplete(
          trackingManager,
          option.value,
          window.location.href,
          false,
          model
        );
      }
    }
  };

  // Detect generate-pdf query param and trigger PDF generation
  // Check for presence only - value doesn't matter
  useEffect(() => {
    if (!hasInit || !isInsideGuide || !hasGeneratePdfParam || isGeneratingPdf) {
      return;
    }

    const generatePdf = async () => {
      if (!qsgNavigation) return;

      setIsGeneratingPdf(true);
      setPdfProgress(0);

      try {
        // Wait a bit for hidden template to render and load sections
        await new Promise((resolve) => setTimeout(resolve, 500));

        const modelId = qsgNavigation.modelId ?? '-';
        const modelYear = qsgNavigation.modelYear ?? '-';

        const pdfBlob = await generatePdfFromDom({
          filename: `quick-start-guide-${modelId}-${modelYear}.pdf`,
          scale: 1,
          waitTime: 150,
          maxWaitTime: 30000,
          onProgress: (progress) => {
            setPdfProgress(progress);
          },
        });

        downloadBlob(pdfBlob, `quick-start-guide-${modelId}-${modelYear}.pdf`);

        // Remove generate-pdf param from URL
        deleteUrlParam(history, 'generate-pdf');
      } catch (error) {
        console.error('Error generating PDF:', error);
      } finally {
        setIsGeneratingPdf(false);
        setPdfProgress(0);
      }
    };

    generatePdf();
  }, [
    hasInit,
    isInsideGuide,
    hasGeneratePdfParam,
    isGeneratingPdf,
    qsgNavigation,
    history,
  ]);

  return (
    <DisclaimerProvider>
      <GuideLayout $isPrintMode={isPrintMode}>
        {!isPrintMode && (
          <Navigation
            onChangeModel={changeModel}
            headerToggle={stickyHeaderToggle}
            isSidebarOpen={isSidebarOpen}
            goToSection={goToSection}
            onSearch={onSubmitSearch}
          />
        )}
        {isPrintMode ? (
          <FullQSGTemplate isPrintMode={isPrintMode} hasInit={hasInit} />
        ) : (
          <Content
            handleChangeHeaderToggle={handleChangeHeaderToggle}
            openSidebar={openSidebar}
            goToSection={goToSection}
            goToSectionFromSearch={goToSectionFromSearch}
            isSidebarOpen={isSidebarOpen}
            scrollToTop={scrollToTop}
            onSearch={onSubmitSearch}
          />
        )}

        {isGeneratingPdf && (
          <Box
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
          >
            <Box
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                padding: '2rem',
                minWidth: '300px',
                maxWidth: '400px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <Box
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  marginBottom: '0.5rem',
                }}
              >
                Generating PDF...
              </Box>
              <Box
                style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: '#e0e0e0',
                  borderRadius: '4px',
                  overflow: 'hidden',
                }}
              >
                <Box
                  style={{
                    width: `${pdfProgress}%`,
                    height: '100%',
                    backgroundColor: '#1976d2',
                    transition: 'width 0.3s ease',
                  }}
                />
              </Box>
              <Box
                style={{
                  fontSize: '0.875rem',
                  color: '#666666',
                }}
              >
                {pdfProgress}%
              </Box>
            </Box>
          </Box>
        )}
      </GuideLayout>
    </DisclaimerProvider>
  );
};
